import { PermissionedRouteObject } from "@redotech/redo-merchant-app-common/routing/protected-routes";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { breadcrumbSlot } from "@redotech/redo-web/breadcrumb";
import { memo, useContext } from "react";
import { matchPath, Navigate, useLocation } from "react-router-dom";
import { TranslatedBreadcrumbLabel } from "../../../return/returns-breadcrumb-label";
import {
  type RoutePageComponent,
  SessionReplayRoutePage,
} from "./session-replay-route-page";

const customersBreadcrumb = breadcrumbSlot();
const allCustomersBreadcrumb = breadcrumbSlot();
const customerRecordBreadcrumb = breadcrumbSlot();

const HIDE_PADDING_ROUTES: string[] = ["/all-customers", "/:customerId"];

function hidePadding(url: string): boolean {
  return HIDE_PADDING_ROUTES.some(
    (route) => !!matchPath(`/stores/:storeId/customer${route}`, url),
  );
}

const FULLSCREEN_ROUTES: string[] = [];

const HIDE_HEADER_ROUTES: string[] = [...FULLSCREEN_ROUTES];
function hideHeader(url: string): boolean {
  return HIDE_HEADER_ROUTES.some(
    (route) => !!matchPath(`/stores/:storeId/customer${route}`, url),
  );
}

const HIDE_NAV_ROUTES: string[] = [...FULLSCREEN_ROUTES];

function hideNav(url: string): boolean {
  return HIDE_NAV_ROUTES.some(
    (route) => !!matchPath(`/stores/:storeId/customer${route}`, url),
  );
}

function isRecognizeRoute(pathname: string, search: string): boolean {
  const searchParams = new URLSearchParams(search);
  return (
    !!matchPath("/stores/:storeId/customer/recognize-lookup", pathname) ||
    (!!matchPath("/stores/:storeId/customer/all-customers", pathname) &&
      searchParams.get("tab") === "recognize")
  );
}

const CustomersRoutesShell = memo(function CustomersRoutesShell({
  Page,
}: {
  Page: RoutePageComponent;
}) {
  const location = useLocation();
  const team = useContext(TeamContext);
  const recognizeEnabled = team?.settings.recognize?.enabled === true;

  return (
    <SessionReplayRoutePage
      enabled={
        recognizeEnabled && isRecognizeRoute(location.pathname, location.search)
      }
      hideHeader={hideHeader}
      hideNavbar={hideNav}
      hidePadding={hidePadding}
      Page={Page}
      productId="recognize"
    />
  );
});

export const customersRoutes: PermissionedRouteObject[] = [
  {
    handle: { breadcrumb: customersBreadcrumb },
    async lazy() {
      const { Page } = await import("../../page");
      return {
        element: (
          <>
            <TranslatedBreadcrumbLabel
              breadcrumb={customersBreadcrumb}
              getLabel={(t) => t("Customers")}
            />
            <CustomersRoutesShell Page={Page} />
          </>
        ),
      };
    },
    children: [
      { index: true, element: <Navigate replace to="all-customers" /> },
      {
        path: "all-customers",
        handle: { breadcrumb: allCustomersBreadcrumb },
        async lazy() {
          const { CustomersPage } =
            // redont-allow-dynamic-import: route-level code splitting
            await import("@redotech/redo-merchant-customer-app/customers-page");
          return {
            element: (
              <>
                <TranslatedBreadcrumbLabel
                  breadcrumb={allCustomersBreadcrumb}
                  getLabel={(t) => t("All customers")}
                />
                <CustomersPage />
              </>
            ),
          };
        },
      },
      {
        handle: { breadcrumb: customerRecordBreadcrumb },
        path: ":customerId",
        async lazy() {
          const { CustomerRecord } =
            await import("@redotech/redo-merchant-customer-app/customer-record/customer-record-page");
          return {
            element: <CustomerRecord breadcrumb={customerRecordBreadcrumb} />,
          };
        },
      },
    ],
  },
];
